import React, {Component} from 'react';
import { Table, Input, FormGroup, Label } from 'reactstrap';
import ImClient from "../service/ImClientSingleton"

class LowInventoryManagementPage extends Component {
    constructor(props) {
        super(props);

        this.state = {
            products: [],
            filteredProducts: [],
            kidFilter: '',
            sortBy: 'kid',
            sortDirection: 'asc',
            loading: true,
            editingThreshold: {},
            thresholdValues: {},
            savingStates: {}
        }
    }

    componentDidMount() {
        this.fetchProducts();
    }

    fetchProducts(){
        this.setState({loading: true});
        ImClient.getProductsWithLowInventoryAlerts({noCache: true})
            .then(products => {
                this.setState({
                    products: products,
                    filteredProducts: products,
                    loading: false
                }, () => {
                    this.sortProducts();
                });
            })
            .catch(err => {
                console.error("Error fetching products with alerts:", err);
                this.setState({loading: false});
            });
    }

    handleFilterChange = (e) => {
        const kidFilter = e.target.value.toUpperCase();
        this.setState({kidFilter}, () => {
            this.filterProducts();
        });
    }

    filterProducts = () => {
        const {products, kidFilter} = this.state;

        let filtered = products;
        if (kidFilter) {
            filtered = products.filter(p =>
                p.key.kid.includes(kidFilter)
            );
        }

        this.setState({filteredProducts: filtered}, () => {
            this.sortProducts();
        });
    }

    handleSort = (column) => {
        const {sortBy, sortDirection} = this.state;

        if (sortBy === column) {
            this.setState({
                sortDirection: sortDirection === 'asc' ? 'desc' : 'asc'
            }, () => {
                this.sortProducts();
            });
        } else {
            this.setState({
                sortBy: column,
                sortDirection: 'asc'
            }, () => {
                this.sortProducts();
            });
        }
    }

    sortProducts = () => {
        const {filteredProducts, sortBy, sortDirection} = this.state;

        const sorted = [...filteredProducts].sort((a, b) => {
            let aVal, bVal;

            if (sortBy === 'kid') {
                aVal = a.key.id;
                bVal = b.key.id;
            } else if (sortBy === 'lastAlert') {
                aVal = a.alertHistory?.lowInventory?.lastAlertSent
                    ? new Date(a.alertHistory.lowInventory.lastAlertSent).getTime()
                    : 0;
                bVal = b.alertHistory?.lowInventory?.lastAlertSent
                    ? new Date(b.alertHistory.lowInventory.lastAlertSent).getTime()
                    : 0;
            }

            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        this.setState({filteredProducts: sorted});
    }

    getCurrentCount = (product) => {
        const alertType = product.detail.alerts.lowInventory.alertType;
        const counters = product.counters;

        if (!counters) return 'N/A';

        switch(alertType) {
            case 'INVENTORY_ONLY':
                return counters.inventory || 0;
            case 'RESERVED_ONLY':
                return counters.storeReserved || 0;
            case 'COMBINED':
                return (counters.inventory || 0) + (counters.storeReserved || 0);
            default:
                return 'N/A';
        }
    }

    formatAlertType = (alertType) => {
        switch(alertType) {
            case 'INVENTORY_ONLY':
                return 'Inventory Only';
            case 'RESERVED_ONLY':
                return 'Reserved Only';
            case 'COMBINED':
                return 'Combined';
            default:
                return alertType;
        }
    }

    formatDate = (dateString) => {
        if (!dateString) return 'Never';
        const date = new Date(dateString);
        return date.toLocaleString();
    }

    handleToggleEnabled = (product, currentlyEnabled) => {
        const productId = product.key.id;

        if (this.state.savingStates[`enable-${productId}`] || this.state.savingStates[`threshold-${productId}`] || this.state.editingThreshold[productId]) {
            return;
        }

        const newEnabledState = !currentlyEnabled;

        const patch = {
            detail: {
                alerts: {
                    lowInventory: {
                        enabled: newEnabledState,
                        alertType: product.detail.alerts.lowInventory.alertType,
                        threshold: product.detail.alerts.lowInventory.threshold
                    }
                }
            }
        };

        this.setState(prevState => ({
            savingStates: {...prevState.savingStates, [`enable-${productId}`]: true}
        }));

        ImClient.patchProduct({productId, patchRequest: patch})
            .then(updatedProduct => {
                const updatedProducts = this.state.products.map(p => {
                    if (p.key.id === productId) {
                        p.detail.alerts.lowInventory.enabled = newEnabledState;
                    }
                    return p;
                });

                this.setState({
                    products: updatedProducts,
                    savingStates: {...this.state.savingStates, [`enable-${productId}`]: false}
                }, () => {
                    this.filterProducts();
                });
            })
            .catch(err => {
                console.error("Error toggling alert:", err);
                const errorMsg = err.message || err.body?.message || 'Unknown error occurred';
                alert(`Failed to ${newEnabledState ? 'enable' : 'disable'} alert: ${errorMsg}`);
                this.setState({
                    savingStates: {...this.state.savingStates, [`enable-${productId}`]: false}
                });
            });
    }

    handleStartEditThreshold = (productId, currentThreshold) => {
        if (this.state.savingStates[`enable-${productId}`] || this.state.savingStates[`threshold-${productId}`]) {
            return;
        }

        this.setState(prevState => ({
            editingThreshold: {...prevState.editingThreshold, [productId]: true},
            thresholdValues: {...prevState.thresholdValues, [productId]: currentThreshold}
        }));
    }

    handleCancelEditThreshold = (productId) => {
        this.setState(prevState => ({
            editingThreshold: {...prevState.editingThreshold, [productId]: false},
            thresholdValues: {...prevState.thresholdValues, [productId]: undefined}
        }));
    }

    handleThresholdInputChange = (productId, value) => {
        this.setState(prevState => ({
            thresholdValues: {...prevState.thresholdValues, [productId]: value}
        }));
    }

    handleSaveThreshold = (product) => {
        const productId = product.key.id;
        const thresholdInput = this.state.thresholdValues[productId];

        if (!thresholdInput || thresholdInput.toString().trim() === '') {
            alert('Threshold cannot be empty');
            return;
        }

        const newThreshold = parseInt(thresholdInput);

        if (isNaN(newThreshold)) {
            alert('Threshold must be a valid number');
            return;
        }

        if (newThreshold < 0) {
            alert('Threshold cannot be negative');
            return;
        }

        if (newThreshold > 100000) {
            alert('Threshold seems unusually high. Please enter a realistic value (max 100,000)');
            return;
        }

        if (thresholdInput.toString().includes('.')) {
            if (!window.confirm(`You entered ${thresholdInput}. Decimals will be rounded to ${newThreshold}. Continue?`)) {
                return;
            }
        }

        const patch = {
            detail: {
                alerts: {
                    lowInventory: {
                        enabled: product.detail.alerts.lowInventory.enabled,
                        alertType: product.detail.alerts.lowInventory.alertType,
                        threshold: newThreshold
                    }
                }
            }
        };

        this.setState(prevState => ({
            savingStates: {...prevState.savingStates, [`threshold-${productId}`]: true}
        }));

        ImClient.patchProduct({productId, patchRequest: patch})
            .then(updatedProduct => {
                const updatedProducts = this.state.products.map(p => {
                    if (p.key.id === productId) {
                        p.detail.alerts.lowInventory.threshold = newThreshold;
                    }
                    return p;
                });

                this.setState({
                    products: updatedProducts,
                    editingThreshold: {...this.state.editingThreshold, [productId]: false},
                    savingStates: {...this.state.savingStates, [`threshold-${productId}`]: false}
                }, () => {
                    this.filterProducts();
                });
            })
            .catch(err => {
                console.error("Error updating threshold:", err);
                const errorMsg = err.message || err.body?.message || 'Unknown error occurred';
                alert(`Failed to update threshold: ${errorMsg}`);
                this.setState({
                    savingStates: {...this.state.savingStates, [`threshold-${productId}`]: false}
                });
            });
    }

    renderTableRows() {
        const {filteredProducts, editingThreshold, thresholdValues, savingStates} = this.state;

        if (filteredProducts.length === 0) {
            return (
                <tr>
                    <td colSpan="7" className="text-center">No products with low inventory alerts configured</td>
                </tr>
            );
        }

        return filteredProducts.map(product => {
            const alert = product.detail.alerts.lowInventory;
            const alertHistory = product.alertHistory?.lowInventory;
            const productId = product.key.id;
            const isEditingThreshold = editingThreshold[productId];
            const isSavingEnable = savingStates[`enable-${productId}`];
            const isSavingThreshold = savingStates[`threshold-${productId}`];
            const isAnySaving = isSavingEnable || isSavingThreshold;

            const rowStyle = alert.enabled ? {} : {backgroundColor: '#f8f9fa', opacity: 0.7};

            return (
                <tr key={productId} style={rowStyle}>
                    <td>
                        <a href={`/packing/${productId}`} target="_blank" rel="noopener noreferrer">
                            {product.key.kid}
                        </a>
                        {!alert.enabled && <span className="badge badge-secondary ml-2">Disabled</span>}
                    </td>
                    <td>{product.detail.name}</td>
                    <td>{this.formatAlertType(alert.alertType)}</td>
                    <td>{this.getCurrentCount(product)}</td>
                    <td>
                        {isEditingThreshold ? (
                            <div className="d-flex align-items-center">
                                <Input
                                    type="number"
                                    min="0"
                                    value={thresholdValues[productId]}
                                    onChange={(e) => this.handleThresholdInputChange(productId, e.target.value)}
                                    style={{width: '80px', marginRight: '5px'}}
                                    disabled={isAnySaving}
                                />
                            </div>
                        ) : (
                            alert.threshold
                        )}
                    </td>
                    <td>{this.formatDate(alertHistory?.lastAlertSent)}</td>
                    <td>
                        <div className="d-flex flex-column" style={{gap: '5px'}}>
                            <button
                                className={`btn btn-sm ${alert.enabled ? 'btn-warning' : 'btn-success'}`}
                                onClick={() => this.handleToggleEnabled(product, alert.enabled)}
                                disabled={isAnySaving || isEditingThreshold}
                            >
                                {isSavingEnable ? 'Saving...' : (alert.enabled ? 'Disable' : 'Enable')}
                            </button>
                            {isEditingThreshold ? (
                                <div className="d-flex" style={{gap: '3px'}}>
                                    <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => this.handleSaveThreshold(product)}
                                        disabled={isAnySaving}
                                        style={{flex: 1}}
                                    >
                                        {isSavingThreshold ? 'Saving...' : 'Save'}
                                    </button>
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => this.handleCancelEditThreshold(productId)}
                                        disabled={isAnySaving}
                                        style={{flex: 1}}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="btn btn-sm btn-info"
                                    onClick={() => this.handleStartEditThreshold(productId, alert.threshold)}
                                    disabled={isAnySaving}
                                >
                                    Edit Threshold
                                </button>
                            )}
                        </div>
                    </td>
                </tr>
            );
        });
    }

    render() {
        const {kidFilter, loading, sortBy, sortDirection} = this.state;

        const getSortIcon = (column) => {
            if (sortBy !== column) return '';
            return sortDirection === 'asc' ? ' ↑' : ' ↓';
        };

        return (
            <div className="App-Width">
                <h1>Low Inventory Management</h1>

                <FormGroup>
                    <Label for="kidFilter">Filter by KID:</Label>
                    <Input
                        type="text"
                        id="kidFilter"
                        placeholder="Enter KID (e.g., K45)"
                        value={kidFilter}
                        onChange={this.handleFilterChange}
                        style={{maxWidth: '300px'}}
                    />
                </FormGroup>

                {loading ? (
                    <p>Loading products...</p>
                ) : (
                    <Table striped>
                        <thead>
                            <tr>
                                <th
                                    onClick={() => this.handleSort('kid')}
                                    style={{cursor: 'pointer'}}
                                >
                                    KID{getSortIcon('kid')}
                                </th>
                                <th>Product Name</th>
                                <th>Alert Type</th>
                                <th>Current Count</th>
                                <th>Alert Threshold</th>
                                <th
                                    onClick={() => this.handleSort('lastAlert')}
                                    style={{cursor: 'pointer'}}
                                >
                                    Last Sent Alert{getSortIcon('lastAlert')}
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.renderTableRows()}
                        </tbody>
                    </Table>
                )}
            </div>
        );
    }
}

export default LowInventoryManagementPage;
